import React, { useState, useEffect, useRef } from 'react';
import {
  useFirebaseContext,
  useTargetListAddContext,
  useActiveListContext,
  useDateTimeContext,
  useAllInOneContext,
} from '../../../context';
import { ListChoices } from './list-choices';
import { checkListsExist } from '../../../hooks';
import { getDate, getTime, resizeOnInput } from '../../../helpers';
import { Float } from '../../float';
import { timestamp } from '../../../lib';
import { ButtonContainer } from '../../layouts/styles';
import { TimePicker } from '../../time_picker';
import { Core } from '../..';
import '../../core/styles/styles.css';
import { COLORS } from '../../../constants';
import { CSSTransition } from 'react-transition-group';
import { ListChoiceRedirect } from './styles';
import { NewDatePicker } from '../../modified_date_picker';
import {
  FaClock,
  FaRegCalendarAlt,
  FaStar,
  FaChevronRight,
} from 'react-icons/fa';

export const AddTask = ({ hasScroll, addTaskRef = null }) => {
  const { firebase } = useFirebaseContext();
  const {
    lists,
    setLists,
    allTasks,
    setAllTasks,
    setMaxModifiedAt,
  } = useAllInOneContext();
  const {
    activeList,
    setActiveList,
    themeColor,
    themeColor_L2,
    themeColor_D2
  } = useActiveListContext();
  const taskTitle = useRef();
  const [show, setShow] = useState(false);
  const [showLists, setShowLists] = useState(false);
  const [progress, setProgress] = useState(0);
  const { listsExist } = checkListsExist();
  const { targetList, setDefaultList, listName } = useTargetListAddContext();
  const [invalid, setInvalid] = useState(false);
  const [isInAdd, setIsInAdd] = useState(false);
  const [task, setTask] = useState();
  const [note, setNote] = useState();
  const [important, setImportant] = useState();
  const [forceCloseNotYet, setForceCloseNotYet] = useState(true);

  const {
    dueDate,
    setDueDate,
    hour,

    minute,

    period,
    setPeriod,
    showDate,
    setShowDate,
    showTime,
    setShowTime,
    resetDate,
    resetTime,
    toggleDate,
    toggleTime,
    changeHour,
    changeMinute,
  } = useDateTimeContext();

  const [colorIndex, setColorIndex] = useState();

  useEffect(() => {
    if (targetList && lists) {
      const index = lists.findIndex((ele) => ele.docID == targetList);

      if (index > -1) setColorIndex(index);
    }
  }, [targetList, lists, showLists]);

  useEffect(() => {
    resetAll();
  }, []);

  useEffect(() => {
    if (addTaskRef && addTaskRef.current) {
      if (listsExist)
        addTaskRef.current.onclick = () => {
          setShow(true);
          setDefaultList(true);
        };
      else addTaskRef.current.onclick = () => {};
    }
  }, [listsExist]);

  useEffect(() => {
    if (taskTitle && taskTitle.current && show) taskTitle.current.focus();
  }, [show, taskTitle]);

  useEffect(() => {
    if (task != '') setInvalid(false);
    else setInvalid(true);
  }, [task]);

  useEffect(() => {
    closeAll();
  }, [activeList]);

  const resetAll = () => {
    setTask('');
    setNote('');
    setImportant(false);
    resetDate();
    resetTime();
    setForceCloseNotYet(true);
  };

  useEffect(() => {
    if (!forceCloseNotYet) {
      closeAll();
      resetAll();
    }
  }, [forceCloseNotYet]);

  const closeAll = () => {
    setShow(false);
    setShowLists(false);
    setShowDate(false);
    setShowTime(false);
  };

  const addTask = () => {
    if (task && targetList) {
      const userDB = firebase
        .firestore()
        .collection('users')
        .doc(firebase.auth().currentUser.uid);
      const taskDB = userDB.collection('tasks');
      const tempModifiedAt = timestamp.now().toMillis();
      const index = lists.findIndex((item) => item.docID == targetList);
      const temp = lists;
      const count = temp[index].taskCount;
      setProgress((100 / 7) * 1);

      const newTaskObj = {
        order: count,
        important: important,
        listID: targetList,
        title: task,
        note: note,
        date: getDate(showDate, dueDate),
        time: getTime(showTime, hour, minute, period),
        modifiedAt: tempModifiedAt,
      };
      temp[index].taskCount = count + 1;
      temp[index].modifiedAt = tempModifiedAt;
      setProgress((100 / 7) * 2);

      userDB
        .collection('lists')
        .doc(targetList)
        .update({ taskCount: count + 1, modifiedAt: tempModifiedAt });
      setProgress((100 / 7) * 3);

      taskDB.add(newTaskObj).then((ref) => {
        setProgress((100 / 7) * 4);
        newTaskObj.docID = ref.id;

        setLists(temp);
        setProgress((100 / 7) * 5);

        const allTempTasks = allTasks;
        allTempTasks.push(newTaskObj);
        setAllTasks(allTempTasks);
        setProgress((100 / 7) * 6);

        setActiveList();
        setActiveList(activeList);
        setProgress(100);

        setIsInAdd(false);
        setMaxModifiedAt(tempModifiedAt);
        closeAll();
        resetAll();
      });
    }
  };

  return (
    <>
      <Core.ForceAlert
        progress={progress}
        setProgress={setProgress}
        color={themeColor}
        showBox={isInAdd}
      >
        Adding the task...
      </Core.ForceAlert>

      {!addTaskRef && (
        <ButtonContainer position='right'>
          <Float.BotButton
            color={themeColor}
            colorD2={themeColor_D2}
            hasScroll={hasScroll}
            disabled={!listsExist}
            onClick={() => {
              setShow(true);
              setDefaultList(true);
            }}
          >
            <span className='button-icon'></span>
            <span className='button-label'>Add Task</span>
          </Float.BotButton>
        </ButtonContainer>
      )}

      <Float.Card
        disabled={invalid}
        color={themeColor}
        color_L2={themeColor_L2}
        showBox={show}
        type='Task'
        setShowBox={setForceCloseNotYet}
      >
        <Float.CardChild type='Task'>
          <Core.Slider type='Task' showBox={showLists}>
            <CSSTransition
              in={!showLists}
              timeout={300}
              classNames='display'
              unmountOnExit
            >
              <div className='target-child'>
                <div className='title'>New Task</div>

                <div className='block'>
                  <div
                    style={{
                      display: 'flex',
                      borderBottom: '1px solid rgba(0,0,0,0.1)',
                      padding: '4px',
                    }}
                  >
                    <Core.TextArea
                      type='text'
                      value={task}
                      onChange={(e) => {
                        resizeOnInput(e.target);
                        setTask(e.target.value);
                      }}
                      placeholder='Task Name'
                      forwardedRef={taskTitle}
                      rows='1'
                      style={{ width: '100%' }}
                    ></Core.TextArea>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      borderTop: '1px solid rgba(0,0,0,0.1)',
                      padding: '4px',
                    }}
                  >
                    <Core.TextArea
                      type='text'
                      placeholder='Note'
                      onChange={(e) => {
                        resizeOnInput(e.target);
                        setNote(e.target.value);
                      }}
                      style={{ width: '100%' }}
                    ></Core.TextArea>
                  </div>
                </div>
                <div className='block'>
                  <div style={{ padding: '10px 15px' }}>
                    <Float.OptionHeader
                      color={themeColor}
                      value={showDate}
                      fun={() => toggleDate()}
                    >
                      <div
                        style={{
                          display: 'flex',

                          alignItems: 'center',
                        }}
                      >
                        <span
                          style={{
                            background: '#ea4e3d',
                            color: 'white',
                            padding: '5px',
                            borderRadius: '5px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginRight: '10px',
                            boxShadow: '2px 2px 4px rgb(0 0 0 / 15%)',
                          }}
                        >
                          <FaRegCalendarAlt />
                        </span>
                        <span>Date</span>
                      </div>
                    </Float.OptionHeader>

                    {showDate && (
                      <div
                        style={{
                          justifyContent: 'center',
                          display: 'flex',
                          paddingTop: '10px',
                        }}
                      >
                        <NewDatePicker
                          themeColor={themeColor}
                          dueDate={dueDate}
                          setDueDate={setDueDate}
                        />
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      padding: '10px 15px',

                      borderTop: '2px solid rgba(0,0,0,0.1)',
                    }}
                  >
                    <Float.OptionHeader
                      color={themeColor}
                      value={showTime}
                      fun={() => toggleTime()}
                    >
                      <div
                        style={{
                          display: 'flex',

                          alignItems: 'center',
                        }}
                      >
                        <span
                          style={{
                            background: '#3478f6',
                            color: 'white',
                            padding: '5px',
                            borderRadius: '5px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginRight: '10px',
                            boxShadow: '2px 2px 4px rgb(0 0 0 / 15%)',
                          }}
                        >
                          <FaClock />
                        </span>
                        <span>Time</span>
                      </div>
                    </Float.OptionHeader>
                    {showTime && (
                      <div
                        style={{
                          justifyContent: 'center',
                          display: 'flex',
                          paddingTop: '10px',
                        }}
                      >
                        <TimePicker
                          hour={hour}
                          changeHour={changeHour}
                          minute={minute}
                          changeMinute={changeMinute}
                          period={period}
                          setPeriod={setPeriod}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className='block' style={{ padding: '10px 15px' }}>
                  <Float.OptionHeader
                    color={themeColor}
                    value={important}
                    fun={() => setImportant(!important)}
                  >
                    <div
                      style={{
                        display: 'flex',

                        alignItems: 'center',
                      }}
                    >
                      <span
                        style={{
                          background: '#f09a37',
                          color: 'white',
                          padding: '5px',
                          borderRadius: '5px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          marginRight: '10px',
                          boxShadow: '2px 2px 4px rgb(0 0 0 / 15%)',
                        }}
                      >
                        <FaStar />
                      </span>
                      <span>Important</span>
                    </div>
                  </Float.OptionHeader>
                </div>
                <div className='block'>
                  {targetList && lists && lists[colorIndex] && (
                    <ListChoiceRedirect
                      color={COLORS[lists[colorIndex].color]}
                      color_L2={themeColor_L2}
                      onClick={() => {
                        setShowLists(true);
                      }}
                    >
                      <div>List</div>
                      <div>
                        <div>
                          <span className='list-dot'></span>
                          <span>{listName}</span>
                        </div>
                        <span
                          style={{
                            marginLeft: '10px',
                            color: 'rgba(0,0,0,0.35)',
                          }}
                        >
                          <FaChevronRight />
                        </span>
                      </div>
                    </ListChoiceRedirect>
                  )}
                </div>
              </div>
            </CSSTransition>

            <CSSTransition
              in={showLists}
              timeout={300}
              classNames='display'
              unmountOnExit
            >
              <ListChoices setShowLists={setShowLists} />
            </CSSTransition>
          </Core.Slider>
        </Float.CardChild>
        <div>
          <div
            onClick={() => {
              setIsInAdd(true);
              addTask();
            }}
          >
            Add
          </div>

          <div
            onClick={() => {
              closeAll();
              resetAll();
            }}
          >
            Cancel
          </div>
        </div>
      </Float.Card>
    </>
  );
};
